import { GLOBAL_CONTEXT_MARKER } from '@constants'
import {
  DUPLICATE_OF_KEY,
  PUBLISHED_KEY,
  SITE_PUBLISH_STATUS_KEY,
  VERTICAL_PUBLISH_STATUS_KEY,
} from '@constants/fieldKeys'
import { PropertyItem } from '@generated/graphql'

export enum PublishStatusOverride {
  Inherit = 'INHERIT',
  Published = 'PUBLISHED',
  Unpublished = 'UNPUBLISHED',
}

/** Which tier decided the effective status. */
export enum PublishStatusScope {
  Site = 'SITE',
  Vertical = 'VERTICAL',
  Network = 'NETWORK',
}

/**
 * Read a property at one exact context, with no fallback.
 *
 * `getProperty` below deliberately falls back across contexts, which is wrong
 * for the publish tiers: "no row at this site" means inherit, and must not
 * silently resolve to the vertical or network row.
 */
export function getPropertyAtContext<T extends Partial<PropertyItem>>({
  key,
  properties,
  contextId,
}: {
  key: string
  properties?: T[]
  contextId?: string
}) {
  if (!contextId) return undefined
  return properties?.find(
    property => property.key === key && property.contextId === contextId,
  )
}

function readOverride<T extends Partial<PropertyItem>>({
  key,
  properties,
  contextId,
}: {
  key: string
  properties?: T[]
  contextId?: string
}): PublishStatusOverride {
  const value = getPropertyAtContext({ key, properties, contextId })?.data
    ?.value
  return value === PublishStatusOverride.Published ||
    value === PublishStatusOverride.Unpublished
    ? value
    : PublishStatusOverride.Inherit
}

/**
 * Resolve the publish tiers the same way the Manager does: nearest
 * non-INHERIT context wins, site over vertical over the network `published`
 * boolean, and UNPUBLISHED is a hard veto.
 *
 * MUST stay in step with `resolvePublishStatusOverride` and `isPublished` in
 * hyperfusion `apps/manager/src/lib/resource/publishing.ts`. If the two drift,
 * this panel confidently reports a state the sites do not have.
 *
 * `published` here is the intent recorded in the CMS. The Manager still
 * applies compliance gates (restricted countries, verticals, parent state)
 * that can veto a PUBLISHED override, so treat this as intended state, not
 * proof the page is live.
 */
export interface IResolvedPublishStatus {
  published: boolean
  scope: PublishStatusScope
  site: PublishStatusOverride
  vertical: PublishStatusOverride
  network: boolean
}

/**
 * The resolution itself, over already-read tier values.
 *
 * Kept separate so callers holding unsaved edits can resolve them without a
 * second copy of the precedence rules.
 */
export function resolvePublishStatusFromTiers({
  site,
  vertical,
  network,
}: {
  site: PublishStatusOverride
  vertical: PublishStatusOverride
  network: boolean
}): IResolvedPublishStatus {
  const winner =
    site !== PublishStatusOverride.Inherit
      ? { value: site, scope: PublishStatusScope.Site }
      : vertical !== PublishStatusOverride.Inherit
      ? { value: vertical, scope: PublishStatusScope.Vertical }
      : null

  return {
    published: winner
      ? winner.value === PublishStatusOverride.Published
      : network,
    scope: winner ? winner.scope : PublishStatusScope.Network,
    site,
    vertical,
    network,
  }
}

export function resolvePublishStatus<T extends Partial<PropertyItem>>({
  properties,
  networkId,
  companyId,
  verticalId,
  siteId,
}: {
  properties?: T[]
  networkId?: string
  companyId?: string
  verticalId?: string
  siteId?: string
}): IResolvedPublishStatus {
  return resolvePublishStatusFromTiers({
    site: readOverride({
      key: SITE_PUBLISH_STATUS_KEY,
      properties,
      contextId: siteId,
    }),
    vertical: readOverride({
      key: VERTICAL_PUBLISH_STATUS_KEY,
      properties,
      contextId: verticalId,
    }),
    /* Same context set as checkPublishedResource, so the network tier shown
     * here cannot disagree with the sidebar's own published state. */
    network: !!getPropertyValue({
      key: PUBLISHED_KEY,
      properties,
      networkId,
      companyId,
      verticalId,
    }),
  })
}

/** Every site-tier override on this resource, keyed by site id. */
export function getSiteOverrides<T extends Partial<PropertyItem>>(
  properties?: T[],
): Record<string, PublishStatusOverride> {
  return (properties ?? []).reduce((acc, property) => {
    if (property.key !== SITE_PUBLISH_STATUS_KEY || !property.contextId)
      return acc
    const value = property.data?.value
    if (
      value !== PublishStatusOverride.Published &&
      value !== PublishStatusOverride.Unpublished
    )
      return acc
    return { ...acc, [property.contextId]: value }
  }, {})
}

export function getProperty<T extends Partial<PropertyItem>>({
  key,
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  key: string
  properties?: T[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  const contextIds = [verticalId, networkId, companyId].filter(Boolean)

  return (
    properties?.find(
      property =>
        property.key === key &&
        (contextIds.length
          ? contextIds.includes(property.contextId)
          : property.contextId === GLOBAL_CONTEXT_MARKER ||
            !property.contextId),
    ) || properties?.find(property => property.key === key)
  )
}

export function getPropertyValue<T extends Partial<PropertyItem>>({
  key,
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  key: string
  properties?: T[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  return getProperty({ key, properties, networkId, companyId, verticalId })
    ?.data?.value
}

export function checkPublishedResource({
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  properties: PropertyItem[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  if (
    checkDuplicateResource({
      properties,
      networkId,
      companyId,
      verticalId,
    })
  )
    return false

  return !!getPropertyValue({
    key: PUBLISHED_KEY,
    properties,
    networkId,
    companyId,
    verticalId,
  })
}

export function checkDuplicateResource({
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  properties: PropertyItem[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  return !!getPropertyValue({
    key: DUPLICATE_OF_KEY,
    properties,
    networkId,
    companyId,
    verticalId,
  })
}

export function getPropertyUpdatedAt<T extends Partial<PropertyItem>>({
  key,
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  key: string
  properties?: T[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  return getProperty({ key, properties, networkId, companyId, verticalId })
    ?.updatedAt
}

export function getPublishedUpdatedAt({
  properties,
  networkId,
  companyId,
  verticalId,
}: {
  properties: PropertyItem[]
  networkId?: string
  companyId?: string
  verticalId?: string
}) {
  return getPropertyUpdatedAt({
    key: PUBLISHED_KEY,
    properties,
    networkId,
    companyId,
    verticalId,
  })
}
